import {readString} from 'react-papaparse';

import {isDeepEqual} from 'src/utils/isDeepEqual';

import {MATCHING_LIST} from '../constants';

import type {UserCRMIDType} from '../typedef';

const readFile = (file: File): Promise<string> => {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();

        reader.onload = event => {
            if (event.target) {
                resolve(event.target.result as string);
            } else {
                reject(new Error('Event target is null.'));
            }
        };

        reader.onerror = error => {
            reject(error);
        };

        reader.readAsText(file);
    });
};

const parseCSV = <T>(fileContent: string, header: boolean): Promise<T[]> => {
    return new Promise((resolve, reject) => {
        readString(fileContent, {
            header,
            dynamicTyping: true,
            delimiter: ',',
            skipEmptyLines: true,
            quoteChar: '"',
            complete: results => {
                if (results.errors.length > 0) {
                    reject(results.errors);
                } else {
                    if (!results.data.length) {
                        reject(new Error('The file is empty.'));
                    }
                    resolve(results.data.map(row => {
                        if (Array.isArray(row)) {
                            return row.map(cell => (cell ? cell.toString().trim() : ''));
                        } else if (row instanceof Object) {
                            const entries = Object.entries(row);
                            const trimmedEntries = entries.map(([key, value]) => [
                                key ? key.toString().trim() : '',
                                value ? value.toString().trim() : '',
                            ]);

                            return Object.fromEntries(trimmedEntries);
                        } else {
                            return row;
                        }
                    }) as T[]);
                }
            },
        });
    });
};

export const parseCSVToUserCRMIDType = async (file: File): Promise<UserCRMIDType[]> => {
    const fileContent = await readFile(file);
    const data = await parseCSV<{'Email': string, 'CRM ID': string}>(fileContent, true);
    const mappedData = data.map(item => {
        return {
            userEmail: item.Email.toLowerCase(),
            crmId: item['CRM ID'],
        };
    });
    return mappedData as UserCRMIDType[];
};

export const validateBulkUploadCrmIdsFile = async (file: File): Promise<boolean> => {
    const fileContent = await readFile(file);
    const data = await parseCSV(fileContent, false);
    const expectedHeaders = MATCHING_LIST.map(item => item.caption);
    if (isDeepEqual({prev: expectedHeaders, current: data[0]})) {
        return true;
    } else {
        throw new Error('File can\'t be parsed. Please make sure the file matches the template.');
    }
};

